import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FileText, Shield, Trash2, ArrowLeft, Mail } from 'lucide-react'

interface LegalLayoutProps {
  title: string
  subtitle: string
  lastUpdated: string
  children: ReactNode
}


export default function LegalLayout({
  title,
  subtitle,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  const location = useLocation()
  const path = location.pathname

  const isTerms = path.includes('terminos') || path.includes('terms')
  const isPrivacy = path.includes('privacidad') || path.includes('privacy')
  const isDeletion = path.includes('eliminacion') || path.includes('data-deletion')

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col text-slate-800">
      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors mr-2"
                title="Volver al inicio"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline text-sm font-medium">Inicio</span>
              </Link>
              <div className="h-5 w-px bg-slate-200 hidden sm:block" />
              <Link to="/" className="flex items-center">
                <img
                  src="/new/IridiaChat_Logo_Fondo_Claro_Transparente.png"
                  alt="IridiaChat"
                  className="h-8 w-auto object-contain"
                />
                <span className="sr-only">IridiaChat</span>
              </Link>
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                Centro Legal
              </span>
            </div>

            <nav className="flex items-center space-x-1 sm:space-x-2">
              <Link
                to="/login"
                className="text-sm font-medium text-slate-600 hover:text-emerald-700 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Acceder al Panel
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Sub-header / Pill Tabs */}
      <div className="bg-white border-b border-slate-200/80 shadow-xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 overflow-x-auto py-1">
              <Link
                to="/terminos"
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isTerms
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Términos de Servicio</span>
              </Link>
              <Link
                to="/privacidad"
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isPrivacy
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span>Política de Privacidad</span>
              </Link>
              <Link
                to="/eliminacion-datos"
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  isDeletion
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
              >
                <Trash2 className="w-4 h-4" />
                <span>Eliminación de Datos</span>
              </Link>
            </div>
            <div className="text-xs text-slate-500 font-medium">
              Vigencia / Actualización: {lastUpdated}
            </div>
          </div>
        </div>
      </div>

      {/* Main Document Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-xs p-6 sm:p-10 lg:p-12">
          {/* Header */}
          <div className="border-b border-slate-200 pb-6 mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              {title}
            </h1>
            <p className="mt-2 text-base sm:text-lg text-slate-600">
              {subtitle}
            </p>
          </div>

          {/* Children body */}
          <div className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-emerald-700 prose-a:no-underline hover:prose-a:underline">
            {children}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <div className="text-center md:text-left space-y-0.5">
            <div>
              © {new Date().getFullYear()} <strong>IridiaChat</strong> · Operado por <strong>INVERSIONES MERCADO NARANJA, C.A.</strong> (RIF J-502314539).
            </div>
            <div className="text-slate-400">
              Caracas, Venezuela · Dominio oficial: <a href="https://iridiachat.com" className="hover:text-emerald-700 underline">iridiachat.com</a>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link to="/terminos" className="hover:text-emerald-700 transition-colors">Términos</Link>
            <Link to="/privacidad" className="hover:text-emerald-700 transition-colors">Privacidad</Link>
            <Link to="/eliminacion-datos" className="hover:text-emerald-700 transition-colors">Eliminación de Datos</Link>
            <a
              href="mailto:soporte@iridiachat.com"
              className="inline-flex items-center gap-1 hover:text-emerald-700 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              <span>soporte@iridiachat.com</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
